import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import ChevronIcon from 'components/ReusableComponents/Icons/Chevron'
import ReplyIcon from 'components/ReusableComponents/Icons/Reply'
import ThumbUpIcon from 'components/ReusableComponents/Icons/ThumbUp'
import TimeSaveIcon from 'components/ReusableComponents/Icons/TimeSave'
import AnalyticTabs from 'features/Analytics/AnalyticTab'
import ConversationsGraph from 'features/Engagement/ConversationsGraph'
import EmployeePerformance from 'features/Engagement/EmployeePerformance'
import EmployeePerformanceDialog from 'features/Engagement/EmployeePerformanceDialog'
import FactNumbers from 'features/Engagement/FactNumbers'
import MessagesByCategory from 'features/Engagement/MessagesByCategory'
import MessagesByTopic from 'features/Engagement/MessagesByTopic'
import ResponseTime from 'features/Engagement/ResponseTime'
import TripAdvisorRatingDialog from 'features/Engagement/TripAdvisorRatingDialog'
import useAllHotelsToggle from 'hooks/useAllHotels'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import engagementApi from 'services/api/engagement'

import styles from './Engagement.module.scss'
import MessagesByTopicDialog from './MessagesByTopicDialog'

const agentResponseTimeInSeconds = 360

function secondsToDhms(seconds) {
  seconds = Number(seconds)
  var d = Math.floor(seconds / (3600 * 24))
  var h = Math.floor((seconds % (3600 * 24)) / 3600)
  var m = Math.floor((seconds % 3600) / 60)

  var dDisplay = d > 0 ? d + 'd ' : ''
  var hDisplay = h + 'h '
  var mDisplay = m + 'm'
  return dDisplay + hDisplay + mDisplay
}

function secondsToMs(seconds) {
  seconds = Number(seconds)
  var d = Math.floor(seconds / (3600 * 24))
  var h = Math.floor((seconds % (3600 * 24)) / 3600)
  var m = Math.floor((seconds % 3600) / 60)
  var s = Math.floor(seconds % 60)

  var dDisplay = d > 0 ? d + 'd ' : ''
  var hDisplay = h > 0 ? h + 'h ' : ''
  var mDisplay = m + 'm '
  var sDisplay = s + 's'
  return dDisplay + hDisplay + mDisplay + sDisplay
}

const Engagement = () => {
  const [startDate, setStartDate] = useState(moment().subtract('14', 'days'))
  const [endDate, setEndDate] = useState(moment())

  const [statsStatus, setStatsStatus] = useState('pending')
  const [stats, setStats] = useState()

  const {
    showAllHotels,
    setShowAllHotels,
    showWholeGroup,
    setShowWholeGroup,
    idsOfCurrentGroup,
    idsOfAllHotels
  } = useAllHotelsToggle()

  const forSubsidiaries = useMemo(() => {
    if (showAllHotels) {
      return idsOfAllHotels
    }

    if (showWholeGroup) {
      return idsOfCurrentGroup
    }

    return []
  }, [idsOfAllHotels, idsOfCurrentGroup, showAllHotels, showWholeGroup])

  useEffect(() => {
    const abortController = new AbortController()

    setStatsStatus('pending')
    engagementApi
      .stats(
        {
          subsidiaryIds: forSubsidiaries,
          fromDate: startDate.format('YYYY-MM-DD'),
          toDate: endDate.format('YYYY-MM-DD')
        },
        abortController
      )
      .then(stats => {
        setStats(stats)
        setStatsStatus('fetched')
      })
      .catch(err => {
        if (!err.toString().includes('The user aborted a request.')) {
          setStatsStatus('erred')
        }
      })

    return () => abortController.abort()
  }, [startDate, endDate, showAllHotels, showWholeGroup, forSubsidiaries])

  const [isEmployeePerformanceDialogVisible, setisEmployeePerformanceDialogVisible] =
    useState(false)

  const [isMessagesByTopicDialogVisible, setIsMessagesByTopicDialogVisible] =
    useState(false)

  const [isTripAdvisorDialogVisible, setIsTripAdvisorDialogVisible] = useState(false)

  return (
    <div>
      <CustomHeader width={1180}>
        <div className={styles.header}>
          <Link to="/analytics">Analytics</Link>
          <ChevronIcon /> Engagement
        </div>
      </CustomHeader>
      {isEmployeePerformanceDialogVisible && (
        <EmployeePerformanceDialog
          onCloseClick={() => setisEmployeePerformanceDialogVisible(false)}
          hotelIds={forSubsidiaries}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      {isMessagesByTopicDialogVisible && (
        <MessagesByTopicDialog
          onCloseClick={() => setIsMessagesByTopicDialogVisible(false)}
          hotelIds={forSubsidiaries}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          startDate={startDate}
          endDate={endDate}
        />
      )}
      {isTripAdvisorDialogVisible && (
        <TripAdvisorRatingDialog
          onCloseClick={() => setIsTripAdvisorDialogVisible(false)}
        />
      )}
      <div className={styles.wrapper}>
        <AnalyticTabs
          showWholeGroup={showWholeGroup}
          setShowWholeGroup={setShowWholeGroup}
          setShowAllHotels={setShowAllHotels}
          showAllHotels={showAllHotels}
          fromDate={startDate.toDate()}
          toDate={endDate.toDate()}
          setFromDate={date => setStartDate(moment(date))}
          setToDate={date => setEndDate(moment(date))}
        />

        <div className={styles.content}>
          <div className={styles.row}>
            <FactNumbers>
              {statsStatus === 'fetched' && (
                <>
                  <FactNumbers.Item
                    Icon={ThumbUpIcon}
                    value={stats.totalServiceOutreaches}
                    label="guests helped via chat"
                  />
                  <FactNumbers.Item
                    Icon={TimeSaveIcon}
                    value={secondsToDhms(
                      stats.totalReplyBackMessagesByBot * agentResponseTimeInSeconds
                    )}
                    label="time saved by chatbot"
                  />
                  <FactNumbers.Item
                    Icon={ReplyIcon}
                    value={`${Math.round(stats.guestEngagementRate * 100)}%`}
                    label="guest engagement rate"
                  />
                </>
              )}
            </FactNumbers>
          </div>
          <div className={styles.row}>
            {/* <TripAdvisorRating onOpenDetails={() => setIsTripAdvisorDialogVisible(true)} /> */}
            <ConversationsGraph
              startDate={startDate}
              endDate={endDate}
              stats={stats}
              statsStatus={statsStatus}
              hotelIds={forSubsidiaries}
            />
          </div>
          <div className={styles.row}>
            <div>
              <ResponseTime
                value={
                  stats?.cumulativeResponseTimeInSeconds && stats?.totalRepliedBackChats
                    ? secondsToMs(
                        stats?.cumulativeResponseTimeInSeconds
                          ? stats.cumulativeResponseTimeInSeconds /
                              stats.totalRepliedBackChats
                          : 0
                      )
                    : secondsToMs(0)
                }
                isLoading={statsStatus === 'pending'}
              />
              <MessagesByCategory
                startDate={startDate}
                endDate={endDate}
                hotelIds={forSubsidiaries}
              />
            </div>
            <div className={styles.col}>
              <EmployeePerformance
                onOpenDetails={() => setisEmployeePerformanceDialogVisible(true)}
                startDate={startDate}
                endDate={endDate}
                hotelIds={forSubsidiaries}
                seeAllHotels={showAllHotels}
              />
              <MessagesByTopic
                onOpenDetails={() => setIsMessagesByTopicDialogVisible(true)}
                startDate={startDate}
                endDate={endDate}
                hotelIds={forSubsidiaries}
              />
            </div>
          </div>
          <div></div>
        </div>
      </div>
    </div>
  )
}

export default Engagement
